import * as React from 'react';
import { cloneDeep } from 'lodash';
import { message, Empty, Popover, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import {
  Attribute,
  ParameterConfig,
  PromptInfo,
  PromptType,
  DiffStates
} from '@interface/index';
import { ReferenceTextArea } from '@component/index';
import '@style/component/response.less';
import { localizedString } from '@locale/index';
import { isEqual, isStructAgent } from '@lib/index';
import { ConflictPop, DiffDrawer } from '@component/diff';
import { defaultInputHeight } from '@config/styledVar';

export const VisiblePromptComponents: { [key: string]: PromptListComp } = {};

export interface PromptListCompProps {
  rich?: boolean;
  globalKey?: string;
  style?: React.CSSProperties;
  agentId?: string;
  frameId: string;
  ownerId: string;
  ownerType: string;
  promptType: PromptType;
  data: PromptInfo[];
  masterData?: PromptInfo[];
  delData?: PromptInfo[];
  parameters?: ParameterConfig[];
  bodyItTypeId?: string;
  update?: (next: PromptInfo[], oriData: PromptInfo[]) => void;
  placeholder?: string;
  placeholderFrist?: string;
  className?: string;
  borderTop?: boolean;
  disableChange?: boolean;
  noReference?: boolean;
  parameterMap: { [key: string]: ParameterConfig };
  isSingleLine?: boolean;
  allowClearAll?: Boolean;
}

export interface PromptListCompState {
  data: PromptInfo[];
  diffVisible: boolean;
  diffLocal?: string;
  diffMaster?: string;
  diffRevert?: () => void;
}

export class PromptListComp extends React.Component<
  PromptListCompProps,
  PromptListCompState
> {
  $wrap?: HTMLDivElement;
  $list?: HTMLUListElement;

  constructor(props: PromptListCompProps) {
    super(props);
    this.state = {
      data: [],
      diffVisible: false,
      diffLocal: undefined,
      diffMaster: undefined,
      diffRevert: undefined
    };
  }

  onInit = () => {
    const props = this.props;
    const nextData = (cloneDeep(props.data) || [])
      .filter((item: PromptInfo) =>
        !item.toBeDeleted);
    nextData.reverse();

    if ((nextData.length
      && nextData[0].prompt !== '' && !props.rich)
      || nextData.length === 0
      && !props.disableChange) {
      nextData.unshift({
        id: '',
        agentId: props.agentId,
        frameId: props.frameId,
        ownerId: props.ownerId,
        ownerType: props.ownerType,
        promptType: props.promptType,
        prompt: ''
      });
    }

    this.setState({ data: nextData });
  }

  componentDidMount() {
    if (this.props.globalKey) {
      VisiblePromptComponents[this.props.globalKey] = this;
    }
    this.onInit();
  }

  componentWillUnmount(): void {

    if (this.props.globalKey) {
      delete VisiblePromptComponents[this.props.globalKey];
    }
  }

  componentDidUpdate(prevProps: any) {
    if (!isEqual(prevProps.data, this.props.data)) {
      this.onInit();
    }
  }

  render() {
    const {
      parameters = [],
      placeholder,
      placeholderFrist,
      className = '',
      borderTop = false,
      disableChange = false,
      noReference = false,
      parameterMap,
      isSingleLine = false,
      allowClearAll = false,
      delData = [],
      style,
      rich
    } = this.props;

    const {
      data,
      diffVisible,
      diffLocal,
      diffMaster,
      diffRevert
    } = this.state;

    const showData = [...data, ...delData];

    return (
      <div
        style={style}
        ref={(wrap: HTMLDivElement) => {
          this.$wrap = wrap;
        }}
        className="text-response__wrap"
      >
        <ul
          ref={(list: HTMLUListElement) => {
            this.$list = list;
          }}
          className={`response__list text-response__list  ${data.length ? '' : 'disabled'}`}
          style={{
            borderTop: borderTop ? 'solid 1px rgba(0, 0, 0, 0.12)' : '',
            borderRadius: borderTop ? '4px' : '',
            minHeight: defaultInputHeight
          }}
        >
          {showData.length ? showData.map(
            (prompt: PromptInfo, index: number) => {
              return (
                <li key={`text-response-${index}`}>
                  {/* <span>{index + 1}</span> */}
                  <ReferenceTextArea
                    forList
                    rich={rich}
                    rows={rich ? 30 : 1}
                    // style={{ marginTop: index === 0 ? 5 : 0 }}
                    disableReferenceMode
                    parameterMap={parameterMap}
                    bodyItTypeId={this.props.bodyItTypeId}
                    readOnly={disableChange ||
                      !!prompt.originKey ||
                      prompt.state === DiffStates.DEL}
                    initParameters={parameters}
                    autoSize={true}
                    placeholder={
                      (
                        index === 0
                          ? (placeholderFrist || placeholder)
                          : placeholder
                      ) || (
                        noReference
                          ? localizedString('text_response_placeholder')
                          : `Enter text, use '\${slot}' or '\${raw code}' to reference parameter values`
                      )
                    }
                    disabled={
                      disableChange ||
                      !!prompt.originKey ||
                      !!prompt.inheritedFromCompositeId ||
                      prompt.state === DiffStates.DEL
                    }
                    className={`text-response__textarea ${className} ${prompt.state === DiffStates.DEL ? 'text-del' : ''
                      }`}
                    data={prompt.prompt}
                    update={(next: string) => {
                      if (next?.trim() === prompt.prompt?.trim()) {
                        return;
                      }
                      prompt.prompt = next?.trim();
                      this.handleBlur(prompt, index);
                    }}
                    onEnter={(value: string) => {
                      setTimeout(() => {
                        if (!isSingleLine && !rich) {
                          this.handleEnter();
                        }
                      }, 500);
                    }}
                  />
                  {
                    !prompt.originKey &&
                    !disableChange &&
                    prompt.state === DiffStates.PRIVATE_CONFLICT &&
                    <ConflictPop onClick={() => {
                      this.setState({
                        diffVisible: true,
                        diffLocal: prompt.prompt,
                        diffMaster: (this.getMasterItem(prompt) || {}).prompt,
                        diffRevert: () => {
                          const masterItem = this.getMasterItem(prompt);
                          if (!masterItem) {
                            return;
                          }
                          masterItem.state = DiffStates.NORMAL;
                          const nextData: PromptInfo[] = cloneDeep(data);
                          nextData[index] = masterItem;
                          this.setState({ data: nextData }, () => {
                            this.$update(nextData);
                          });
                        }
                      });
                    }}
                    />
                  }
                  {
                    !prompt.originKey &&
                    !disableChange &&
                    !prompt.inheritedFromCompositeId &&
                    prompt.state === DiffStates.DEL &&
                    <ConflictPop onClick={() => {
                      this.setState({
                        diffVisible: true,
                        diffLocal: undefined,
                        diffMaster: prompt.prompt,
                        diffRevert: () => {
                          const nextItem = cloneDeep(prompt);
                          nextItem.state = DiffStates.NORMAL;
                          const nextData: PromptInfo[] = cloneDeep(data);
                          nextData.unshift(nextItem);
                          this.setState({ data: nextData }, () => {
                            this.$update(nextData);
                          });
                        }
                      });
                    }}
                    />
                  }
                  {disableChange ||
                    !!prompt.originKey ||
                    !!prompt.inheritedFromCompositeId ||
                    isSingleLine ||
                    data.length < 1
                    || (index === 0 && !prompt.id) ? (
                    ''
                  ) : (
                    <DeleteOutlined
                      style={{
                        color: 'rgba(0, 0, 0, 0.54)'
                      }}
                      onClick={() => {
                        const nextData = cloneDeep(data);
                        nextData.splice(index, 1);
                        this.setState({ data: nextData });
                        this.$update(nextData);
                      }}
                    />
                  )}
                </li>
              );
            }
          ) : <li>
            <ReferenceTextArea
              disabled
              disableReferenceMode
              forList
              data={''}
              initParameters={[]}
              parameterMap={{}}
              style={{ marginTop: 9 }}
              placeholder={isStructAgent() ? localizedString('language_dependency_hint') : localizedString('text_response_placeholder')}
              update={() => {
                // do nothing
              }}
              className={`text-response__textarea`}
            />
          </li>}
        </ul>
        <DiffDrawer
          basePath={[]}
          visible={diffVisible}
          localData={diffLocal}
          masterData={diffMaster}
          onRevert={diffRevert}
        />
      </div>
    );
  }

  getMasterItem = (localItem: PromptInfo) => {
    const masterData = this.props.masterData || [];
    return masterData.find((item: PromptInfo) => item.id === localItem.id);
  }

  autoGen(value: string) {
    const { data } = this.state;
    const props = this.props;

    const next = cloneDeep(data);
    next[0] = {
      id: '',
      agentId: props.agentId,
      frameId: props.frameId,
      ownerId: props.ownerId,
      ownerType: props.ownerType,
      promptType: props.promptType,
      prompt: value
    };

    this.setState({ data: next }, () => {
      this.$update(next);
    });
  }

  handleEnter = () => {
    const props = this.props;
    const { data } = this.state;
    const target: PromptInfo[] = cloneDeep(data);

    if (target[0].prompt !== '' && !props.disableChange) {
      target.unshift({
        id: '',
        agentId: props.agentId,
        frameId: props.frameId,
        ownerId: props.ownerId,
        ownerType: props.ownerType,
        promptType: props.promptType,
        prompt: ''
      });
    }

    this.setState({ data: target }, () => {
      const doms: any = this.$list && this.$list.querySelectorAll(
        '.text-response__textarea textarea'
      );

      if (doms[0]) {
        doms[0].focus();
      }
    });
  };

  handleBlur = (value: PromptInfo, index: number) => {
    const { data } = this.state;

    const target: PromptInfo[] = cloneDeep(data);

    const responseMap: { [key: string]: number } = {};

    target[index] = value;

    target.forEach((prt: PromptInfo) => {
      if (!responseMap[prt.prompt]) {
        responseMap[prt.prompt] = 1;
      } else {
        responseMap[prt.prompt] += 1;
      }
    });

    if (responseMap[value.prompt] > 1) {
      message.warning(localizedString('text_response_repeated'));
      target.splice(index, 1);
    }

    this.setState({ data: target }, () => {
      this.$update(target);
    });
  };

  $update = (next: PromptInfo[]) => {
    const { update } = this.props;

    const originData = this.props.data || [];
    const nextData = cloneDeep(next.filter((item: PromptInfo) =>
      item.prompt !== ''));
    nextData.reverse();

    for (const oldItem of originData) {
      if (!next.find((item: PromptInfo) =>
        item.id === oldItem.id)) {
        const delItem = {
          ...oldItem,
          toBeDeleted: true
        };
        nextData.push(delItem);
      }
    }

    if (update) {
      update(nextData, this.props.data);
    }
  };
}
